import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { LogOut, Home, FileText, Save } from 'lucide-react';

const AdminDashboard = () => {
  const [content, setContent] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
    fetchContent();
    fetchProjects();
  }, []);

  const checkAuth = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
    }
  };

  const fetchContent = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/homepage-content`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setContent(data);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      toast.error('İçerik yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/admin/projects', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Projects fetch error:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/homepage-content`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(content),
      });

      if (response.ok) {
        toast.success('Değişiklikler kaydedildi!');
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
    toast.success('Çıkış yapıldı');
  };

  const handleChange = (field, value) => {
    setContent({ ...content, [field]: value });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-[#1e4a7a]">MİGA Yapı - Admin Panel</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/" target="_blank">
                <Button variant="outline" size="sm">
                  <Home className="h-4 w-4 mr-2" />
                  Siteyi Görüntüle
                </Button>
              </Link>
              <Button onClick={handleLogout} variant="outline" size="sm">
                <LogOut className="h-4 w-4 mr-2" />
                Çıkış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 py-4 overflow-x-auto">
            <Link 
              to="/admin/dashboard" 
              className="text-[#1e4a7a] font-medium border-b-2 border-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Ana Sayfa İçeriği
            </Link>
            <Link 
              to="/admin/about" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Kurumsal
            </Link>
            <Link 
              to="/admin/services" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Hizmetlerimiz
            </Link>
            <Link 
              to="/admin/contact" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              İletişim
            </Link>
            <Link 
              to="/admin/faq" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              SSS
            </Link>
            <Link 
              to="/admin/projects" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Proje Yönetimi
            </Link>
            <Link 
              to="/admin/testimonials" 
              className="text-gray-600 hover:text-[#1e4a7a] pb-4 whitespace-nowrap"
            >
              Müşteri Referansları
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Ana Sayfa İçeriği</h2>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-[#dc2626] hover:bg-[#b91c1c]"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
            </Button>
          </div>

          <div className="space-y-8">
            {/* Hero Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Hero Bölümü</h3>
              <div className="space-y-4">
                <div>
                  <Label>Ana Slogan</Label>
                  <Input
                    value={content?.hero_slogan || ''}
                    onChange={(e) => handleChange('hero_slogan', e.target.value)}
                    className="mt-1"
                    placeholder="Hayallerinizi Güvenle İnşa Ediyoruz"
                  />
                </div>
                <div>
                  <Label>Alt Açıklama</Label>
                  <Textarea
                    value={content?.hero_description || ''}
                    onChange={(e) => handleChange('hero_description', e.target.value)}
                    className="mt-1"
                    rows={3}
                    placeholder="30 yılı aşkın sektör tecrübemiz ve 25'ten fazla başarılı projemizle İstanbul'da kaliteli inşaat hizmetleri sunuyoruz"
                  />
                </div>
              </div>
            </div>

            {/* Statistics */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">İstatistikler</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Yıllık Deneyim</Label>
                  <Input
                    value={content?.stat_years || ''}
                    onChange={(e) => handleChange('stat_years', e.target.value)}
                    className="mt-1"
                    placeholder="30+"
                  />
                </div>
                <div>
                  <Label>Tamamlanan Proje</Label>
                  <Input
                    value={content?.stat_projects || ''}
                    onChange={(e) => handleChange('stat_projects', e.target.value)}
                    className="mt-1"
                    placeholder="25+"
                  />
                </div>
                <div>
                  <Label>Toplam Daire</Label>
                  <Input
                    value={content?.stat_apartments || ''}
                    onChange={(e) => handleChange('stat_apartments', e.target.value)}
                    className="mt-1"
                    placeholder="200+"
                  />
                </div>
                <div>
                  <Label>Mutlu Müşteri</Label>
                  <Input
                    value={content?.stat_clients || ''}
                    onChange={(e) => handleChange('stat_clients', e.target.value)}
                    className="mt-1"
                    placeholder="500+"
                  />
                </div>
              </div>
            </div>

            {/* "Neden MİGA Yapı?" Section */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">"Neden MİGA Yapı?" Bölümü</h3>
              <div className="space-y-4">
                <div>
                  <Label>Başlık</Label>
                  <Input
                    value={content?.why_section_title || ''}
                    onChange={(e) => handleChange('why_section_title', e.target.value)}
                    className="mt-1"
                    placeholder="Neden MİGA Yapı?"
                  />
                </div>
                <div>
                  <Label>Alt Açıklama</Label>
                  <Textarea
                    value={content?.why_section_description || ''}
                    onChange={(e) => handleChange('why_section_description', e.target.value)}
                    className="mt-1"
                    rows={2}
                    placeholder="Sektördeki uzmanlığımız ve müşteri memnuniyeti odaklı yaklaşımımızla fark yaratıyoruz"
                  />
                </div>
              </div>
            </div>

            {/* Featured Projects Selection */}
            <div className="border-b pb-6">
              <h3 className="text-lg font-semibold mb-4">Ana Sayfada Gösterilecek Projeler</h3>
              <p className="text-sm text-gray-600 mb-4">
                Ana sayfada görüntülenecek 3 öne çıkan projeyi seçin
              </p>
              <div className="grid grid-cols-3 gap-4">
                {[0, 1, 2].map((index) => (
                  <div key={index}>
                    <Label>{index + 1}. Proje</Label>
                    <select
                      value={content?.featured_project_ids?.[index] || ''}
                      onChange={(e) => {
                        const newIds = [...(content?.featured_project_ids || ['', '', ''])];
                        newIds[index] = e.target.value;
                        handleChange('featured_project_ids', newIds);
                      }}
                      className="w-full px-3 py-2 border rounded-md mt-1"
                    >
                      <option value="">Proje Seçin</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name} - {project.status}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Özellikler</h3>
              
              {/* Feature 1 */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">Özellik 1</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Başlık</Label>
                    <Input
                      value={content?.feature1_title || ''}
                      onChange={(e) => handleChange('feature1_title', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Açıklama</Label>
                    <Textarea
                      value={content?.feature1_desc || ''}
                      onChange={(e) => handleChange('feature1_desc', e.target.value)}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Feature 2 */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">Özellik 2</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Başlık</Label>
                    <Input
                      value={content?.feature2_title || ''}
                      onChange={(e) => handleChange('feature2_title', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Açıklama</Label>
                    <Textarea
                      value={content?.feature2_desc || ''}
                      onChange={(e) => handleChange('feature2_desc', e.target.value)}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>

              {/* Feature 3 */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">Özellik 3</h4>
                <div className="space-y-3">
                  <div>
                    <Label>Başlık</Label>
                    <Input
                      value={content?.feature3_title || ''}
                      onChange={(e) => handleChange('feature3_title', e.target.value)}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Açıklama</Label>
                    <Textarea
                      value={content?.feature3_desc || ''}
                      onChange={(e) => handleChange('feature3_desc', e.target.value)}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
